import styled, { css } from 'styled-components';
import tw from 'twin.macro';

import {
  AdsTable,
  Checkbox,
  LineChart,
  SoldTable,
  TooltipInfo,
} from '@/components';
import { ReportItems, ROLE_TYPES, IGNORE_LIST_REPORT } from '@/constants';
import { useProjectContext } from '@/context/ProjectContext';
import ColumnChoiceDialog from '@/components/ColumnChoiceDialog';
import { ButtonColor, ColumnEnum, TableSize, TabsType } from '@/enums';
import strings from '@/language/Swedish';
import Market from '../../market/Market';
import { ColumnType, SalesRefTableType } from '@/types/dataTypes';
import { getMarkerLabel,filterLineChartDataBasedOnDateInterval } from '@/helpers';
import useUser from '@/api/queries/useUser';
const Ul = styled.ul`
  ${tw`flex-1 space-y-3`}
`;

const Li = styled.li`
  ${tw`mt-1.5!`}
`;

const DataContainer = styled.div`
  ${tw`relative mt-3 mb-8`}
`;

const DataWrapper = styled.div`
  ${tw`relative p-2 mb-1 bg-white rounded-xl`}
`;

const StyledColumnChoiceDialog = styled.div`
  ${tw`absolute right-2 -top-9`}
`;

const ColumnWarning = styled.span`
  ${tw`pt-6 p-2 text-red-500 font-bold text-sm `}
`;

const ChartWrapper = styled.div`
  ${tw`px-4 py-6 mt-3 mb-8 bg-white rounded-xl`}
`;

const TableStyling = css`
  tr {
    background-color: transparent;
  }
`;

const IndexHeading = styled.h1`
  ${tw`text-base font-normal whitespace-normal mb-0 mr-1 md:whitespace-nowrap`}
`;

const StyledSoldTable = styled(SoldTable)`
  ${TableStyling}
`;

const StyledAdsTable = styled(AdsTable)`
  ${TableStyling}
`;

const Data = () => {
  const {
    project: {
      soldTableData,
      adsTableData,
      adsTableIds,
      soldSummary,
      adsSummary,
      adsTableReportColumns,
      soldTableIds,
      soldTableReportColumns,
      marketAds,
      indexData,
      index_normalized_fields
    },
    selectedReportItems,
    visitedTabs,
    handleReportItemsToggle,
    toggleReportColumnVisibility
  } = useProjectContext();

  const { data: userData } = useUser();

  const soldFilteredTable = soldTableData?.filter((obj) =>
    soldTableIds?.selected?.has(obj.id)
  );
  const adsFilteredTable = adsTableData?.filter((obj) =>
    adsTableIds?.selected?.has(obj.id)
  );


  const soldTable =
    soldFilteredTable?.length === 0 ? soldTableData : soldFilteredTable;
  const adsTable =
    adsFilteredTable?.length === 0 ? adsTableData : adsFilteredTable;

  const hasValue = (value: TabsType) => {
    // First check if the tab has been visited
    const hasVisited = visitedTabs.has(value);
    
    if (!hasVisited) {
      return false;
    }

    // If visited, check if there's actual data
    switch (value) {
      case TabsType.sold:
        return soldFilteredTable ? true : false;

      case TabsType.ads:
        return adsFilteredTable ? true : false;

      case TabsType.priceDevelopment:
        return indexData ? true : false;

      case TabsType.market:
        return marketAds ? true : false;

      default:
        return true;
    }
  };

  const maxColumns = 16;

  const getNrOfColumns = (columns: ColumnType[]) => {
    let columnNr = 0;
    columns.map((column) => {
      if (column.isVisible && !IGNORE_LIST_REPORT.includes(column.id as keyof typeof ColumnEnum | any)) columnNr++;
    });
    return columnNr;
  };

  const updatedsoldTable = soldTable?.map((soldItem, index) => {
    const newSoldItem = soldItem;
    newSoldItem.street = newSoldItem.adress; //FIX IN FUTURE, street is overwritten without street number because of some state in the app
    return {
      ...soldItem,
      id: getMarkerLabel(index),
    };
  }) as SalesRefTableType[];
  return (
    <Ul>
      {ReportItems.map(
        ({ title, value }) => {
          const noMarketRights = value == TabsType.market && !userData?.roles.includes(ROLE_TYPES.MARKET_SHARE_AD);
          return selectedReportItems && (
            <Li key={value}>
              {hasValue(value) && !noMarketRights ? (
                <Checkbox
                  id={value}
                  label={title}
                  disabled={noMarketRights}
                  isChecked={selectedReportItems[value]}
                  onToggle={handleReportItemsToggle}
                />
              ) : (
                <TooltipInfo content={noMarketRights ? strings.market.noMarketRights : (!visitedTabs.has(value) ? 'Besök fliken först för att inkludera i rapporten' : strings.report.noData)}>
                  <div className="inline-block">
                    <Checkbox
                      id={value}
                      label={title}
                      isChecked={selectedReportItems[value]}
                      disabled={!hasValue(value) || noMarketRights}
                      onToggle={handleReportItemsToggle}
                    />
                  </div>
                </TooltipInfo>
              )}
              {selectedReportItems[value] && (
                <>
                  {value === TabsType.sold && soldFilteredTable && (
                    <DataContainer>
                      <StyledColumnChoiceDialog>
                        <ColumnChoiceDialog
                          columns={soldTableReportColumns.filter(col => 
                            col.id !== ColumnEnum.allabrfUrl && 
                            col.id !== ColumnEnum.allabolagUrl && 
                            col.id !== ColumnEnum.prospectUrl && 
                            col.id !== ColumnEnum.housingAssociationReportYear)}
                          color={ButtonColor.primary}
                          toggleColumn={(id: string) =>
                            toggleReportColumnVisibility(TabsType.sold, id)
                          }
                        />
                      </StyledColumnChoiceDialog>
                      <DataWrapper>
                        <StyledSoldTable
                          report={true}
                          tableData={updatedsoldTable || []}
                          tableSummary={soldSummary}
                          size={TableSize.small}
                        />
                      </DataWrapper>
                      {getNrOfColumns(soldTableReportColumns) >= maxColumns && (
                        <ColumnWarning>
                          {strings.table.columnWarning}
                        </ColumnWarning>
                      )}
                    </DataContainer>
                  )}
                  {value === TabsType.ads && adsFilteredTable && (
                    <DataContainer>
                      <StyledColumnChoiceDialog>
                        <ColumnChoiceDialog
                          columns={adsTableReportColumns.filter(col => 
                            col.id !== ColumnEnum.allabrfUrl &&
                            col.id !== ColumnEnum.allabolagUrl &&
                            col.id !== ColumnEnum.prospectUrl &&
                            col.id !== ColumnEnum.housingAssociationReportYear
                          )}
                          color={ButtonColor.primary}
                          toggleColumn={(id: string) =>
                            toggleReportColumnVisibility(TabsType.ads, id)
                          }
                        />
                      </StyledColumnChoiceDialog>
                      <DataWrapper>
                        <StyledAdsTable
                          report={true}
                          tableSummary={adsSummary}
                          tableData={adsTable || []}
                          size={TableSize.small}
                        />
                      </DataWrapper>
                      {getNrOfColumns(adsTableReportColumns) >= maxColumns && (
                        <ColumnWarning>
                          {strings.table.columnWarning}
                        </ColumnWarning>
                      )}
                    </DataContainer>
                  )}
                  {value === TabsType.market && (
                    <ChartWrapper>
                      <Market small />
                    </ChartWrapper>
                  )}
                  {value === TabsType.priceDevelopment && (
                    <ChartWrapper>
                      {indexData && (
                        <>
                          <IndexHeading>
                            {strings.priceDevelopment.pageHeading}{' '}
                            {indexData.short_name}
                          </IndexHeading>
                          <LineChart
                            data={filterLineChartDataBasedOnDateInterval(indexData.values,index_normalized_fields.reference_date_from,index_normalized_fields.reference_date_to)}
                            chartLabel={strings.priceDevelopment.chartLabel}
                          />
                        </>
                      )}
                    </ChartWrapper>
                  )}
                </>
              )}
            </Li>
          )
})}
    </Ul>
  );
};

export default Data;
